// File: src/context/StudentAuthContext.jsx
//
// Global Cognito student-session state — the third leg alongside Auth0's
// own isAuthenticated. App.js branches its route tree on this; any nested
// page (MyProgress, MyPayments, MyAccount) reads it via useAuthToken() to
// know whether to fetch its API token from Cognito or Auth0, since those
// three pages are shared by legacy Auth0 students (pre-migration) and
// Cognito students (post-migration) alike.
//
// Mirrors the existing ActingAsContext.jsx pattern for consistency.

import React, { createContext, useContext, useState, useCallback } from 'react';
import { isStudentSession, studentLogout, getStudentPhone } from '../services/cognitoAuth';

const StudentAuthContext = createContext(null);

export const StudentAuthProvider = ({ children }) => {
  // Initialized from localStorage synchronously so a page refresh doesn't
  // flash the public/login view before this catches up.
  const [isStudentAuthenticated, setIsStudentAuthenticated] = useState(isStudentSession);
  const [studentPhone, setStudentPhone] = useState(getStudentPhone);

  /** Call right after confirmStudentOtp() resolves with { success: true }. */
  const markStudentLoggedIn = useCallback(() => {
    setIsStudentAuthenticated(true);
    setStudentPhone(getStudentPhone());
  }, []);

  /** Clears the Cognito tokens and flips the app back to the public branch. */
  const studentLogoutAndClear = useCallback(() => {
    studentLogout();
    setIsStudentAuthenticated(false);
    setStudentPhone(null);
  }, []);

  return (
    <StudentAuthContext.Provider value={{
      isStudentAuthenticated,
      studentPhone,
      markStudentLoggedIn,
      studentLogoutAndClear
    }}>
      {children}
    </StudentAuthContext.Provider>
  );
};

/**
 * @returns {{
 *   isStudentAuthenticated: boolean,
 *   studentPhone: string | null,
 *   markStudentLoggedIn: () => void,
 *   studentLogoutAndClear: () => void
 * }}
 */
export const useStudentAuth = () => {
  const ctx = useContext(StudentAuthContext);
  if (!ctx) throw new Error('useStudentAuth must be used within a StudentAuthProvider');
  return ctx;
};
